// Modals/UrlNoteModal.jsx
import React, { useEffect, useState, useRef } from "react";
import PropTypes from "prop-types";
import toast from "react-hot-toast";
import { formatRelativeTime, getFullDate } from "../NotesTab/utils/formatUtils";
import { highlightHtmlContent } from "../NotesTab/utils/htmlUtils";
import "./UrlNoteModal.css";

const UrlNoteModal = ({ 
  isOpen, 
  noteId, 
  taskId, 
  itemType, 
  onClose, 
  apiUrl, 
  refreshNotes, 
  userId, 
  searchTerm = "",
  updateNote 
}) => {
  const [note, setNote] = useState(null);
  const [task, setTask] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [editedContent, setEditedContent] = useState("");
  const [editedTaskDescription, setEditedTaskDescription] = useState("");
  const [saving, setSaving] = useState(false);
  const [isDirty, setIsDirty] = useState(false);
  const [isTaskDirty, setIsTaskDirty] = useState(false);
  const textareaRef = useRef(null);
  const taskTextareaRef = useRef(null);

  useEffect(() => {
    if (isOpen) {
      if (itemType === 'task' && taskId) {
        fetchTask();
      } else if (itemType === 'note' && noteId) {
        fetchNote();
      }
    }
  }, [isOpen, noteId, taskId, itemType]);

  // Focus and place cursor at the end when textarea is editable
  useEffect(() => {
    if (isOpen && note && canEditNote() && textareaRef.current) {
      const textarea = textareaRef.current;
      const length = textarea.value.length;
      textarea.focus();
      textarea.setSelectionRange(length, length);
    }
  }, [isOpen, note, editedContent]);

  // Focus task textarea when component loads and user can edit
  useEffect(() => {
    if (isOpen && task && canEditTask() && taskTextareaRef.current) {
      const textarea = taskTextareaRef.current;
      const length = textarea.value.length;
      textarea.focus();
      textarea.setSelectionRange(length, length);
    }
  }, [isOpen, task, editedTaskDescription]);

  const fetchNote = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await fetch(`${apiUrl}/SiteNote/GetSiteNoteById/${noteId}`);
      
      if (!response.ok) {
        throw new Error(`Failed to fetch note: ${response.status}`);
      }
      
      const data = await response.json();
      
      if (data.message === "Success" && data.siteNote) {
        setNote(data.siteNote);
        setEditedContent(data.siteNote.note || "");
        setIsDirty(false);
        setTask(null);
      } else {
        throw new Error('Note not found');
      }
    } catch (error) {
      console.error('Error fetching note:', error);
      setError(error.message);
      toast.error('Failed to load note');
    } finally {
      setLoading(false);
    }
  };

  const fetchTask = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await fetch(`${apiUrl}/JobTasks/${taskId}`);
      
      if (!response.ok) {
        throw new Error(`Failed to fetch task: ${response.status}`);
      }
      
      const data = await response.json();
      
      if (data.success && data.data) {
        setTask(data.data);
        setEditedTaskDescription(data.data.description || "");
        setIsTaskDirty(false);
        setNote(null);
      } else {
        throw new Error(data.message || 'Task not found');
      }
    } catch (error) {
      console.error('Error fetching task:', error);
      setError(error.message);
      toast.error('Failed to load task');
    } finally {
      setLoading(false);
    }
  };

  const handleSaveNote = async () => {
    if (!note || !note.id) return;
    
    setSaving(true);
    try {
      // Use the updateNote function passed from parent
      if (updateNote) {
        const updatedData = {
          Note: editedContent,
          Date: note.timeStamp || note.date,
          TaskId: note.taskId || null,
          UserId: note.userId
        };
        
        await updateNote(note.id, updatedData);
        
        setNote({ ...note, note: editedContent });
        setIsDirty(false);
        toast.success('Note updated successfully');
        if (refreshNotes) {
          refreshNotes();
        }
      } else {
        throw new Error('Update note function not available');
      }
    } catch (error) {
      console.error('Error updating note:', error);
      toast.error(`Failed to update note: ${error.message}`);
    } finally {
      setSaving(false);
    }
  };

  const handleSaveTaskDescription = async () => {
    if (!task || !task.id) return;
    
    setSaving(true);
    try {
      const response = await fetch(`${apiUrl}/JobTasks/${task.id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          ...task,
          description: editedTaskDescription,
        }),
      });

      if (!response.ok) {
        throw new Error(`Failed to update task: ${response.status}`);
      }

      const data = await response.json();
      
      if (data.success) {
        setTask({ ...task, description: editedTaskDescription });
        setIsTaskDirty(false);
        toast.success('Task description updated successfully');
        if (refreshNotes) {
          refreshNotes();
        }
      } else {
        throw new Error(data.message || 'Failed to update task');
      }
    } catch (error) {
      console.error('Error updating task:', error);
      toast.error(`Failed to update task: ${error.message}`);
    } finally {
      setSaving(false);
    }
  };

  const handleCancelEdit = () => {
    setEditedContent(note?.note || "");
    setIsDirty(false);
  };

  const handleContentChange = (e) => {
    setEditedContent(e.target.value);
    setIsDirty(e.target.value !== (note?.note || ""));
  };

  const handleTaskDescriptionChange = (e) => {
    setEditedTaskDescription(e.target.value);
    setIsTaskDirty(e.target.value !== (task?.description || ""));
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleString();
  };

  const formatDateOnly = (dateString) => {
    if (!dateString) return 'Not set';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const getStatusName = (statusId) => {
    const statusMap = {
      1: 'To Do',
      2: 'In Progress',
      3: 'Archived',
      4: 'Blocked',
      5: 'Done'
    };
    return statusMap[statusId] || 'Unknown';
  };

  const getStatusColor = (statusId) => {
    const colorMap = {
      1: '#6c757d',
      2: '#fd7e14',
      3: '#6c757d',
      4: '#dc3545',
      5: '#28a745'
    };
    return colorMap[statusId] || '#6c757d';
  };

  const getStatusBgColor = (statusId) => {
    const colorMap = {
      1: '#e9ecef',
      2: '#fff3e0',
      3: '#e9ecef',
      4: '#f8d7da',
      5: '#d4edda'
    };
    return colorMap[statusId] || '#e9ecef';
  };

  const getInitials = (name) => {
    if (!name) return '?';
    return name.split(' ').map(n => n[0]).join('').toUpperCase().substring(0, 2);
  };

  const isNoteOlderThan24Hours = (noteDate) => {
    if (!noteDate) return true;
    const noteTime = new Date(noteDate).getTime();
    const currentTime = new Date().getTime();
    const hoursDiff = (currentTime - noteTime) / (1000 * 60 * 60);
    return hoursDiff > 24;
  };

  const canEditNote = () => {
    if (!note || !userId) return false;
    
    const noteUserId = String(note.userId);
    const currentUserId = String(userId);
    const isOwner = noteUserId === currentUserId;
    const isOlderThan24Hrs = isNoteOlderThan24Hours(note.timeStamp || note.date);
    
    return isOwner && !isOlderThan24Hrs;
  };

  const canEditTask = () => {
    if (!task || !userId) return false;
    
    const taskCreatedById = String(task.createdById);
    const currentUserId = String(userId);
    
    return taskCreatedById === currentUserId;
  };

  const getEditDisabledReason = () => {
    if (!note || !userId) return "";
    
    const isOwner = String(note.userId) === String(userId);
    const isOlderThan24Hrs = isNoteOlderThan24Hours(note.timeStamp || note.date);
    
    if (!isOwner) return "Only the note owner can edit";
    if (isOlderThan24Hrs) return "Notes older than 24 hours cannot be edited";
    return "";
  };

  const renderTaskContent = () => {
    if (!task) return null;

    const isTaskOwner = canEditTask();
    const formattedDescription = highlightHtmlContent(task.description || 'No description', searchTerm);

    return (
      <div className="url-note-modal-body">
        {/* Task Header */}
        <div className="url-note-task-header">
          <div className="url-note-task-header-left">
            <div 
              className="url-note-task-icon"
              style={{ backgroundColor: getStatusBgColor(task.status), color: getStatusColor(task.status) }}
            >
              <i className={`fas ${task.status === 2 ? 'fa-spinner' : task.status === 5 ? 'fa-check-circle' : 'fa-tasks'}`} />
            </div>
            <div className="url-note-task-title-wrapper">
              <div className="url-note-task-title-row">
                <h3 className="url-note-task-title">
                  {task.title}
                </h3>
                {task.friendlyId && (
                  <span className="url-note-friendly-id">
                    {task.friendlyId}
                  </span>
                )}
                <div className="url-note-task-job">
                  <i className="fas fa-briefcase"></i>
                  {task.jobName}
                </div>
              </div>
            </div>
          </div>
          
          {/* Project and Workspace Info */}
          <div className="url-note-context">
            {task.projectName && (
              <span className="url-note-context-item">
                <i className="fas fa-folder" />
                {task.projectName}
              </span>
            )}
            {task.workspaceName && (
              <span className="url-note-context-item">
                <i className="fas fa-building" />
                {task.workspaceName}
              </span>
            )}
          </div>
        </div>

        {/* Description Section - Same as note component */}
        <div className="url-note-description-section">
          {isTaskOwner ? (
            <textarea
              ref={taskTextareaRef}
              className="url-note-textarea"
              value={editedTaskDescription}
              onChange={handleTaskDescriptionChange}
              disabled={saving}
              placeholder="Enter task description..."
              rows={6}
            />
          ) : (
            <div 
              className="url-note-description-content read-only"
              dangerouslySetInnerHTML={{ __html: formattedDescription }}
            />
          )}
        </div>

        {/* Task Details Grid */}
        <div className="url-note-details-grid">
          <div className="url-note-detail-item">
            <div className="url-note-detail-label">
              <i className="fas fa-circle" style={{ color: getStatusColor(task.status) }} />
              Status
            </div>
            <span 
              className="url-note-status-badge"
              style={{
                backgroundColor: getStatusBgColor(task.status),
                color: getStatusColor(task.status)
              }}
            >
              {getStatusName(task.status)}
            </span>
          </div>

          <div className="url-note-detail-item">
            <div className="url-note-detail-label">
              <i className="fas fa-calendar" />
              Start Date
            </div>
            <div className="url-note-detail-value">
              {formatDateOnly(task.startDate)}
            </div>
          </div>

          <div className="url-note-detail-item">
            <div className="url-note-detail-label">
              <i className="fas fa-calendar-check" />
              End Date
            </div>
            <div className="url-note-detail-value">
              {formatDateOnly(task.endDate)}
            </div>
          </div>

          <div className="url-note-detail-item">
            <div className="url-note-detail-label">
              <i className="fas fa-hourglass-half" />
              Due Date
            </div>
            <div className={`url-note-detail-value ${task.dueDate ? 'has-value' : 'no-value'}`}>
              {formatDateOnly(task.dueDate) || 'Not set'}
            </div>
          </div>
        </div>

        {/* Assigned Users */}
        {task.assignedUsers && task.assignedUsers.length > 0 && (
          <div className="url-note-assigned-section">
            <div className="url-note-assigned-title">
              <i className="fas fa-users" />
              Assigned Users ({task.assignedUsers.length})
            </div>
            <div className="url-note-users-list">
              {task.assignedUsers.map((user, index) => (
                <div key={user.userId || index} className="url-note-user-item">
                  <div className="url-note-user-avatar">
                    {getInitials(user.username)}
                  </div>
                  <span className="url-note-user-name">{user.username}</span>
                  {user.assignedByUsername && (
                    <span className="url-note-assigned-by">
                      (by {user.assignedByUsername})
                    </span>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Footer with Save/Cancel for task description */}
        <div className="url-note-footer">
          <div className="url-note-footer-left">
            <div className="url-note-footer-item">
              <i className="fas fa-clock" />
              Created {formatDate(task.createdAt)}
            </div>
          </div>
          
          {/* Save and Cancel Buttons - Always visible for task owners */}
          {isTaskOwner && (
            <div className="url-note-footer-right">
              <button 
                className="url-note-save-btn"
                onClick={handleSaveTaskDescription}
                disabled={saving || !isTaskDirty}
              >
                {saving ? (
                  <>
                    <i className="fas fa-spinner fa-spin"></i>
                    Saving...
                  </>
                ) : (
                  <>
                    <i className="fas fa-save"></i>
                    Save
                  </>
                )}
              </button>
              <button 
                className="url-note-cancel-btn"
                onClick={() => {
                  setEditedTaskDescription(task?.description || "");
                  setIsTaskDirty(false);
                }}
                disabled={saving || !isTaskDirty}
              >
                Cancel
              </button>
            </div>
          )}
        </div>
      </div>
    );
  };

  const renderNoteContent = () => {
    if (!note) return null;

    const isOwner = canEditNote();
    const disabledReason = getEditDisabledReason();
    const formattedContent = highlightHtmlContent(note.note || 'No content', searchTerm);

    return (
      <div className="url-note-modal-body">
        {/* Note Header */}
        <div className="url-note-header">
          <div className="url-note-header-left">
            <div className="url-note-avatar">
              {getInitials(note.userName)}
            </div>
            <div className="url-note-meta">
              <div className="url-note-author">
                {note.userName || 'Unknown User'}
              </div>
              <div className="url-note-date" title={getFullDate(note.timeStamp || note.date)}>
                {formatRelativeTime(note.timeStamp || note.date)}
              </div>
            </div>
          </div>
          
          {/* Job and Project Info */}
          <div className="url-note-context">
            {note.workspace && (
              <span className="url-note-context-item">
                <i className="fas fa-building" />
                {note.workspace}
              </span>
            )}
            {note.project && (
              <span className="url-note-context-item">
                <i className="fas fa-folder" />
                {note.project}
              </span>
            )}
            {note.job && (
              <span className="url-note-context-item">
                <i className="fas fa-briefcase" />
                {note.job}
              </span>
            )}
          </div>
        </div>

        {/* Note Content */}
        <div className="url-note-content-section">
          {isOwner ? (
            <textarea
              ref={textareaRef}
              className="url-note-textarea"
              value={editedContent}
              onChange={handleContentChange}
              disabled={saving}
              placeholder="Enter note content..."
              rows={4}
            />
          ) : (
            <div 
              className="url-note-content read-only"
              dangerouslySetInnerHTML={{ __html: formattedContent }}
            />
          )}
        </div>

        {/* Disabled reason for non-editable notes */}
        {!isOwner && disabledReason && (
          <div className="url-note-disabled-message">
            <i className="fas fa-info-circle"></i>
            {disabledReason}
          </div>
        )}

        {/* Attachments */}
        {note.documentCount > 0 && (
          <div className="url-note-attachments">
            <i className="fas fa-paperclip" />
            {note.documentCount} attachment{note.documentCount !== 1 ? 's' : ''}
          </div>
        )}

        {/* Footer */}
        <div className="url-note-footer">
          <div className="url-note-footer-left">
            {note.isUpdated && (
              <div className="url-note-footer-item">
                <i className="fas fa-pen" />
                Edited
              </div>
            )}
            <div className="url-note-footer-item">
              <i className="fas fa-reply" />
              {note.replyCount || 0} replies
            </div>
          </div>
         
          {isOwner && (
            <div className="url-note-footer-right">
              <button 
                className="url-note-save-btn"
                onClick={handleSaveNote}
                disabled={saving || !isDirty}
              >
                {saving ? (
                  <>
                    <i className="fas fa-spinner fa-spin"></i>
                    Saving...
                  </>
                ) : (
                  <>
                    <i className="fas fa-save"></i>
                    Save
                  </>
                )}
              </button>
              <button 
                className="url-note-cancel-btn"
                onClick={handleCancelEdit}
                disabled={saving || !isDirty}
              >
                Cancel
              </button>
            </div>
          )}
        </div>
      </div>
    );
  };

  const getTitle = () => {
    if (itemType === 'task') return 'Task Details';
    return 'Note Details';
  };

  const getIcon = () => {
    if (itemType === 'task') return 'fas fa-tasks';
    return 'fas fa-sticky-note';
  };

  if (!isOpen) return null;

  return (
    <div className="url-note-modal-overlay" onClick={onClose}>
      <div className="url-note-modal-container" onClick={(e) => e.stopPropagation()}>
        {/* Modal Header */}
        <div className="url-note-modal-header">
          <h2 className="url-note-modal-title">
            <i className={getIcon()} />
            {getTitle()}
          </h2>
          <button onClick={onClose} className="url-note-close-btn">
            <i className="fas fa-times"></i>
          </button>
        </div>

        {/* Modal Content */}
        {loading ? (
          <div className="url-note-loading">
            <i className="fas fa-spinner fa-spin url-note-spinner"></i>
            <p className="url-note-loading-text">
              Loading {itemType === 'task' ? 'task' : 'note'}...
            </p>
          </div>
        ) : error ? (
          <div className="url-note-error">
            <i className="fas fa-exclamation-circle url-note-error-icon"></i>
            <p className="url-note-error-text">{error}</p>
            <button onClick={onClose} className="url-note-close-error-btn">
              Close
            </button>
          </div>
        ) : (note || task) ? (
          itemType === 'task' ? renderTaskContent() : renderNoteContent()
        ) : (
          <div className="url-note-empty">
            <i className="fas fa-exclamation-circle url-note-empty-icon"></i>
            <p className="url-note-empty-text">Content not found</p>
            <button onClick={onClose} className="url-note-empty-btn">
              Close
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

UrlNoteModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  noteId: PropTypes.number,
  taskId: PropTypes.number,
  itemType: PropTypes.oneOf(['note', 'task']).isRequired,
  onClose: PropTypes.func.isRequired,
  apiUrl: PropTypes.string.isRequired,
  refreshNotes: PropTypes.func,
  userId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  searchTerm: PropTypes.string,
  updateNote: PropTypes.func, 
};

UrlNoteModal.defaultProps = {
  noteId: null,
  taskId: null,
  refreshNotes: () => {},
  userId: null,
  searchTerm: "",
  updateNote: null, 
};

export default UrlNoteModal;

